import StatCard from '../../StatCard/StatCard';
import LegendDoughnutChart from '@/components/Ui/DoughnutChart/LegendDoughnutChart';
import Chart from '@/components/Ui/Chart/Chart';
import { ChartType } from '@/components/Ui/Chart/Chart.types';
import financeYellow from '@/assets/icons/transaction.svg';
import financeGreen from '@/assets/icons/financegreen.svg';
import { useMemo } from 'react';
import { useFinance } from '../useFinance';
import { FadeLoader } from 'react-spinners';
// Composant local pour les graphiques
interface ChartCardProps {
  title: string;
  description: string;
  data: number[];
  labels: string[];
}

const ChartCard = ({ title, description, data, labels }: ChartCardProps) => {
  return (
    <div className="bg-black rounded-[3px]">
      <div className="px-6 pt-4">
        <p className="text-white text-[15.102px] font-normal tracking-[-0.519px]">
          {title}
        </p>
        <p className="text-textColor text-xs font-normal tracking-[-0.519px]">
          {description}
        </p>
      </div>
      <Chart stepSize={1} type={ChartType.BAR} data={data} labels={labels} />
    </div>
  );
};

export const FinanceDashboard = () => {
  const {
    premium,
    salesDistribution,
    subscriptionRevenues,
    isLoading,
    conversion,
    resubscription,
    users,
    trends,
  } = useFinance();

  const statCards = useMemo(
    () => [
      {
        title: 'Total des transactions',
        value: subscriptionRevenues?.totalTransactionAmount ?? 0,
        valueColor: 'text-white',
        showPercentage: false,
        cardStyle: 'border border-primary bg-primary/5',
      },
      {
        title: 'Offre mensuelle',
        value: subscriptionRevenues?.monthly ?? 0,
        valueColor: 'text-white',
        icon: financeYellow,
        styleTitle: 'text-primary font-[700]',
      },
      {
        title: 'Offre annuelle',
        value: subscriptionRevenues?.yearly ?? 0,
        valueColor: 'text-white',
        icon: financeYellow,
        styleTitle: 'text-primary font-[700]',
      },
      {
        title: 'Offre trimestrielle',
        value: subscriptionRevenues?.quarterly ?? 0,
        valueColor: 'text-white',
        icon: financeYellow,
        styleTitle: 'text-primary font-[700]',
      },
      {
        title: 'Offre hebdomadaire',
        value: subscriptionRevenues?.weekly ?? 0,
        valueColor: 'text-white',
        icon: financeYellow,
        styleTitle: 'text-primary font-[700]',
      },
      {
        title: 'Offre Journalière',
        value: subscriptionRevenues?.daily ?? 0,
        valueColor: 'text-white',
        icon: financeYellow,
        styleTitle: 'text-primary font-[700]',
      },
      {
        title: 'Subventions',
        value: 0,
        valueColor: 'text-white',
        icon: financeYellow,
        showDetailsLink: true,
        href: '#',
        studentsCount: 0,
        styleTitle: 'text-primary font-[700]',
      },
      {
        title: 'Taux de conversion',
        value: conversion?.rate?.toFixed(2),
        studentsCount: conversion?.convertedCustomers,
        showPercentage: true,
        valueColor: 'text-white',
        icon: financeGreen,
      },
    ],
    [subscriptionRevenues, conversion],
  );

  const bottomStatCards = useMemo(
    () => [
      {
        title: 'Ex premium',
        value: premium?.exPremium ?? 0,
        valueColor: 'text-white',
        icon: financeGreen,
        showDetailsLink: true,
        href: '#',
      },
      {
        title: 'Taux de Reabonnement',
        value: resubscription?.rate.toFixed(2),
        showPercentage: true,
        valueColor: 'text-white',
        icon: financeGreen,
      },
    ],
    [premium, resubscription, users],
  );

  const salesDistributionItems = useMemo(
    () => [
      {
        label: 'Offre Journalière',
        value: salesDistribution?.daily ?? 0,
        percentageOrSalesCount: `${salesDistribution?.daily ?? 0} ventes`,
        color: 'var(--amberYellow)',
      },
      {
        label: 'Offre Hebdomadaire',
        value: salesDistribution?.weekly ?? 0,
        percentageOrSalesCount: `${salesDistribution?.weekly ?? 0} ventes`,
        color: 'var(--mixedColor)',
      },
      {
        label: 'Offre Mensuelle',
        value: salesDistribution?.monthly ?? 0,
        percentageOrSalesCount: `${salesDistribution?.monthly ?? 0} ventes`,
        color: 'var(--vividRed)',
      },
      {
        label: 'Offre Trimestrielle',
        value: salesDistribution?.quarterly ?? 0,
        percentageOrSalesCount: `${salesDistribution?.quarterly ?? 0} ventes`,
        color: 'var(--mindGreen)',
      },
    ],
    [salesDistribution],
  );

  const userDistributionItems = useMemo(
    () => [
      {
        label: 'candidats libres',
        value: users?.freeCandidates ?? 0,
        percentageOrSalesCount: `${users?.freeCandidates ?? 0} utilisateurs`,
        color: 'var(--amberYellow)',
      },
      {
        label: 'Mode Gratuit',
        value: users?.freeMode ?? 0,
        percentageOrSalesCount: `${users?.freeMode ?? 0} utilisateurs`,
        color: 'var(--vividRed)',
      },
      {
        label: 'Mode Essai',
        value: users?.freeTrialMode ?? 0,
        percentageOrSalesCount: `${users?.freeTrialMode ?? 0} utilisateurs`,
        color: 'var(--mindGreen)',
      },
      {
        label: 'Mode Premium',
        value: users?.premiumMode ?? 0,
        percentageOrSalesCount: `${users?.premiumMode ?? 0} utilisateurs`,
        color: 'var(--violetPurple)',
      },
    ],
    [users],
  );

  const chartCards = useMemo(
    () => [
      {
        title: 'Tendance des revenus',
        description: 'Évolution des revenus dans le temps',
        data: trends.map((item) => item.revenue),
        labels: trends.map((item) => item.period),
      },
      {
        title: 'Réabonnement',
        description: "Nombre d'utilisateur par renouvellement d'abonnement",
        data: resubscription.distribution.map((item) => item.studentCount),
        labels: resubscription.distribution.map(
          (item) => `X${item.resubscriptionCount}`,
        ),
      },
      {
        title: 'Croissance des utilisateurs',
        description: "Nombre d'utilisateur total par région",
        data: [120, 250, 300, 180, 220],
        labels: ['Région 1', 'Région 2', 'Région 3', 'Région 4', 'Région 5'],
      },
    ],
    [trends, users, salesDistribution],
  );

  return (
    <>
      {isLoading ? (
        <div className="w-full h-[100vh] flex items-center justify-center">
          <FadeLoader color="#FEC200" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-y-5 gap-x-4 mb-5 mt-10">
            {statCards.map((card, index) => (
              <StatCard
                key={index}
                {...card}
                value={Number(card.value) ?? undefined}
              />
            ))}
          </div>
          <div className="grid grid-cols-[1fr_2fr_1fr] gap-4">
            <div className="grid grid-cols-1 gap-4">
              {bottomStatCards.map((card, index) => (
                <StatCard
                  key={index}
                  {...card}
                  value={Number(card.value) ?? undefined}
                />
              ))}
            </div>
            <ChartCard {...chartCards[1]} />
            <div className="bg-black rounded-[3px] p-2">
              <LegendDoughnutChart
                styleLegend="mt-[5px]"
                className="mt-5"
                items={salesDistributionItems}
              />
            </div>
          </div>
          <div className="grid grid-cols-[3fr_1fr] gap-4 mt-[30px]">
            <ChartCard {...chartCards[0]} />
            <div className="bg-black rounded-[3px] p-2">
              <LegendDoughnutChart
                styleLegend="mt-[5px]"
                className="mt-5"
                items={userDistributionItems}
              />
            </div>
          </div>
        </>
      )}
    </>
  );
};
